/**
 * Team Dashboard Page
 *
 * Internal team collaboration dashboard with activity feed,
 * project boards, and team member status
 */

import React, { useState } from 'react'
import {
  Users,
  Activity,
  GitBranch,
  GitCommit,
  GitPullRequest,
  CheckCircle,
  Circle,
  AlertCircle,
  Clock,
  MessageSquare,
  Calendar,
  TrendingUp,
  Eye,
  Code
} from 'lucide-react'

interface TeamMember {
  id: string
  name: string
  avatar: string
  status: 'active' | 'away' | 'offline'
  currentTask?: string
  lastSeen: string
}

interface ActivityItem {
  id: string
  type: 'commit' | 'pr' | 'deployment' | 'comment' | 'task'
  user: string
  title: string
  description: string
  timestamp: string
  repo?: string
}

interface KanbanTask {
  id: string
  title: string
  assignee: string
  priority: 'high' | 'medium' | 'low'
  labels: string[]
}

export default function TeamDashboard() {
  const [teamMembers] = useState<TeamMember[]>([
    {
      id: '1',
      name: 'Alex Chen',
      avatar: 'AC',
      status: 'active',
      currentTask: 'MATRIZ safety implementation',
      lastSeen: 'now'
    },
    {
      id: '2',
      name: 'Jordan Rivera',
      avatar: 'JR',
      status: 'active',
      currentTask: 'Website deployment',
      lastSeen: '5m ago'
    },
    {
      id: '3',
      name: 'Sam Taylor',
      avatar: 'ST',
      status: 'away',
      currentTask: 'API documentation',
      lastSeen: '1h ago'
    },
    {
      id: '4',
      name: 'Morgan Lee',
      avatar: 'ML',
      status: 'offline',
      lastSeen: '3h ago'
    }
  ])

  const [activities] = useState<ActivityItem[]>([
    {
      id: '1',
      type: 'commit',
      user: 'Alex Chen',
      title: 'feat(MATRIZ): add RunawayLoopDetector',
      description: 'Implemented safety detector with 100K msg/sec threshold',
      timestamp: '5m ago',
      repo: 'Lukhas'
    },
    {
      id: '2',
      type: 'pr',
      user: 'Jordan Rivera',
      title: 'PR #1825: Website deployment infrastructure',
      description: 'Add docker-compose and nginx routing for all services',
      timestamp: '15m ago',
      repo: 'Lukhas'
    },
    {
      id: '3',
      type: 'deployment',
      user: 'System',
      title: 'lukhas.dev deployed to production',
      description: 'Build #234 deployed successfully',
      timestamp: '32m ago'
    },
    {
      id: '4',
      type: 'comment',
      user: 'Sam Taylor',
      title: 'Comment on Issue #412',
      description: 'Suggested alternative approach for identity tier system',
      timestamp: '1h ago',
      repo: 'Lukhas'
    },
    {
      id: '5',
      type: 'task',
      user: 'Morgan Lee',
      title: 'Completed: API Reference documentation',
      description: 'Added comprehensive OpenAPI docs for all services',
      timestamp: '2h ago'
    }
  ])

  const [kanbanColumns] = useState({
    todo: [
      {
        id: 't1',
        title: 'Implement NIΛS Dream Session processing',
        assignee: 'Alex',
        priority: 'high' as const,
        labels: ['NIΛS', 'backend']
      },
      {
        id: 't2',
        title: 'Add WebSocket support to CoherenceMonitor',
        assignee: 'Jordan',
        priority: 'medium' as const,
        labels: ['frontend', 'websocket']
      }
    ],
    inProgress: [
      {
        id: 'p1',
        title: 'MATRIZ safety component integration',
        assignee: 'Alex',
        priority: 'high' as const,
        labels: ['MATRIZ', 'safety']
      },
      {
        id: 'p2',
        title: 'Website deployment automation',
        assignee: 'Jordan',
        priority: 'high' as const,
        labels: ['devops', 'CI/CD']
      },
      {
        id: 'p3',
        title: 'API documentation review',
        assignee: 'Sam',
        priority: 'medium' as const,
        labels: ['docs', 'API']
      }
    ],
    done: [
      {
        id: 'd1',
        title: 'Identity tier system implementation',
        assignee: 'Alex',
        priority: 'high' as const,
        labels: ['identity', 'backend']
      },
      {
        id: 'd2',
        title: 'lukhas.dev homepage redesign',
        assignee: 'Jordan',
        priority: 'medium' as const,
        labels: ['frontend', 'design']
      }
    ]
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500'
      case 'away': return 'bg-yellow-500'
      case 'offline': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-400 border-red-400/30 bg-red-500/10'
      case 'medium': return 'text-yellow-400 border-yellow-400/30 bg-yellow-500/10'
      case 'low': return 'text-blue-400 border-blue-400/30 bg-blue-500/10'
      default: return 'text-gray-400 border-gray-400/30 bg-gray-500/10'
    }
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'commit': return <GitCommit className="w-4 h-4 text-green-400" />
      case 'pr': return <GitPullRequest className="w-4 h-4 text-blue-400" />
      case 'deployment': return <TrendingUp className="w-4 h-4 text-purple-400" />
      case 'comment': return <MessageSquare className="w-4 h-4 text-cyan-400" />
      case 'task': return <CheckCircle className="w-4 h-4 text-green-400" />
      default: return <Activity className="w-4 h-4 text-gray-400" />
    }
  }

  return (
    <div className="min-h-screen bg-consciousness-deep">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-lambda-gold/20 to-warning-amber/20 rounded-lg">
                <Users className="w-8 h-8 text-lambda-gold" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Team Dashboard</h1>
                <p className="text-gray-400 mt-1">Real-time collaboration & project tracking</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{teamMembers.filter(m => m.status === 'active').length}</p>
                <p className="text-xs text-gray-400">Active Now</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Activity Feed */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg">
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-lambda-gold" />
                    <span>Team Activity</span>
                  </h2>
                  <button className="text-sm text-gray-400 hover:text-white">View All</button>
                </div>
              </div>

              <div className="divide-y divide-gray-800">
                {activities.map((activity) => (
                  <div key={activity.id} className="p-6 hover:bg-gray-800/30 transition-colors">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 mt-1">{getActivityIcon(activity.type)}</div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-white">{activity.title}</p>
                          <span className="text-xs text-gray-500">{activity.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{activity.description}</p>
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <span className="flex items-center space-x-1">
                            <Users className="w-3 h-3" />
                            <span>{activity.user}</span>
                          </span>
                          {activity.repo && (
                            <span className="flex items-center space-x-1">
                              <GitBranch className="w-3 h-3" />
                              <span>{activity.repo}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Kanban Board Preview */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg">
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-lambda-gold" />
                    <span>Sprint Board</span>
                  </h2>
                  <button className="text-sm text-gray-400 hover:text-white flex items-center space-x-1">
                    <Eye className="w-4 h-4" />
                    <span>Full Board</span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-3 gap-4">
                  {/* To Do */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase">To Do</h3>
                      <span className="text-xs text-gray-500">{kanbanColumns.todo.length}</span>
                    </div>
                    <div className="space-y-3">
                      {kanbanColumns.todo.map((task) => (
                        <div key={task.id} className="bg-gray-800/50 border border-gray-700 rounded p-3">
                          <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </div>
                          <p className="text-sm text-white mb-2">{task.title}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {task.labels.map((label, i) => (
                                <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded">
                                  {label}
                                </span>
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">{task.assignee}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* In Progress */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase">In Progress</h3>
                      <span className="text-xs text-gray-500">{kanbanColumns.inProgress.length}</span>
                    </div>
                    <div className="space-y-3">
                      {kanbanColumns.inProgress.map((task) => (
                        <div key={task.id} className="bg-gray-800/50 border border-blue-500/30 rounded p-3">
                          <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </div>
                          <p className="text-sm text-white mb-2">{task.title}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {task.labels.map((label, i) => (
                                <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded">
                                  {label}
                                </span>
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">{task.assignee}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Done */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase">Done</h3>
                      <span className="text-xs text-gray-500">{kanbanColumns.done.length}</span>
                    </div>
                    <div className="space-y-3">
                      {kanbanColumns.done.map((task) => (
                        <div key={task.id} className="bg-gray-800/50 border border-green-500/30 rounded p-3 opacity-75">
                          <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </div>
                          <p className="text-sm text-white mb-2 line-through">{task.title}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {task.labels.map((label, i) => (
                                <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded">
                                  {label}
                                </span>
                              ))}
                            </div>
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Members */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg">
              <div className="p-6 border-b border-gray-800">
                <h2 className="text-xl font-semibold text-white">Team</h2>
              </div>

              <div className="p-6 space-y-4">
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex items-start space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lambda-gold/30 to-warning-amber/30 flex items-center justify-center text-white font-semibold">
                        {member.avatar}
                      </div>
                      <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-900 ${getStatusColor(member.status)}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{member.name}</p>
                      {member.currentTask && (
                        <p className="text-xs text-gray-400 truncate">{member.currentTask}</p>
                      )}
                      <p className="text-xs text-gray-500 flex items-center space-x-1 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{member.lastSeen}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg">
              <div className="p-6 border-b border-gray-800">
                <h2 className="text-xl font-semibold text-white">Quick Stats</h2>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Sprint Progress</span>
                    <span className="text-sm font-medium text-white">12/15</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full">
                    <div className="h-full bg-lambda-gold rounded-full" style={{ width: '80%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Code Coverage</span>
                    <span className="text-sm font-medium text-green-400">87%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '87%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Open PRs</span>
                    <span className="text-sm font-medium text-blue-400">8</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Deployments Today</span>
                    <span className="text-sm font-medium text-purple-400">5</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
